import { ApiService } from "./apiService";
import { failure } from "./serviceHelpers";
import {
  Post,
  Comment,
  PostResponse,
  PostPaginatedResponse,
  PostDetailResponse,
  PostLikersResponse,
  CommentPaginatedResponse,
  CommentDetailResponse,
  CreatePostInput,
  UpdatePostInput,
  CreateCommentInput,
  UpdateCommentInput,
  PostServiceResponse,
  CommentServiceResponse,
  PostAttachment,
  PostType,
  Author,
  ReportedPostsResponse,
} from "../types/community";
import { logger } from "../utils/logger";

export class CommunityService {
  // ========== POST OPERATIONS ==========

  // Get paginated posts list
  static async getPosts(page: number = 1): Promise<PostResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      const response = await ApiService.get(`/posts?${params.toString()}`);
      const result: PostPaginatedResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching posts:", error);
      return null;
    }
  }

  // Search/filter posts by type
  static async searchPostsByType(
    type: PostType,
    page: number = 1,
    challengeId: number | null = null
  ): Promise<PostResponse | null> {
    try {
      const params = new URLSearchParams({
        type: type,
        page: page.toString(),
      });

      if (challengeId !== null) {
        params.append("challenge_id", challengeId.toString());
      }

      const response = await ApiService.get(
        `/posts/search?${params.toString()}`
      );
      const result: PostPaginatedResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error searching posts by type:", error);
      return null;
    }
  }

  // Get single post by ID
  static async getPostById(id: number): Promise<Post | null> {
    try {
      const response = await ApiService.get(`/posts/${id}`);
      const result: PostDetailResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching post:", error);
      return null;
    }
  }

  // Get post likers
  static async getPostLikers(postId: number): Promise<Author[] | null> {
    try {
      const response = await ApiService.get(`/posts/${postId}/likers`);
      const result: PostLikersResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching post likers:", error);
      return null;
    }
  }

  // Get comment likers
  static async getCommentLikers(commentId: number): Promise<Author[] | null> {
    try {
      const response = await ApiService.get(`/comments/${commentId}/likers`);
      const result: PostLikersResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching comment likers:", error);
      return null;
    }
  }

  // Create new post
  static async createPost(
    postData: CreatePostInput
  ): Promise<PostServiceResponse<Post>> {
    try {
      const { attachments = [], ...payload } = postData;

      const response = await ApiService.post("/posts", payload);
      const result: PostDetailResponse = await ApiService.handleResponse(
        response
      );

      let createdPost = result.data;
      let attachmentError: string | undefined;

      if (attachments.length > 0 && createdPost.id) {
        const uploadResult = await this.uploadPostAttachments(
          createdPost.id,
          attachments
        );

        if (!uploadResult.success) {
          attachmentError =
            uploadResult.error || "Failed to upload post attachments";
        } else if (uploadResult.data && uploadResult.data.length > 0) {
          createdPost = {
            ...createdPost,
            attachments: uploadResult.data,
          };
        }
      }

      if (!createdPost.attachments) {
        const refreshedPost = await this.getPostById(createdPost.id);
        if (refreshedPost) {
          createdPost = refreshedPost;
        }
      }

      return {
        success: true,
        data: createdPost,
        ...(attachmentError ? { error: attachmentError } : {}),
      };
    } catch (err) {
      return failure(err, "Failed to create post");
    }
  }

  // Update existing post
  static async updatePost(
    id: number,
    updates: UpdatePostInput
  ): Promise<PostServiceResponse<Post>> {
    try {
      const {
        attachmentsToAdd = [],
        attachmentsToRemove = [],
        ...payload
      } = updates;

      let attachmentError: string | undefined;
      let attachmentsChanged = false;

      if (attachmentsToRemove.length > 0) {
        for (const attachmentId of attachmentsToRemove) {
          const deleteResult = await this.deletePostAttachment(attachmentId);
          if (!deleteResult.success && !attachmentError) {
            attachmentError =
              deleteResult.error || "Failed to delete post attachment";
          } else if (deleteResult.success) {
            attachmentsChanged = true;
          }
        }
      }

      const response = await ApiService.patch(`/posts/${id}`, payload);
      const result: PostDetailResponse = await ApiService.handleResponse(
        response
      );

      let updatedPost = result.data;

      if (!updatedPost.attachments) {
        const refreshedPost = await this.getPostById(id);
        if (refreshedPost) {
          updatedPost = refreshedPost;
        }
        attachmentsChanged = false;
      }

      if (attachmentsToAdd.length > 0) {
        const uploadResult = await this.uploadPostAttachments(
          id,
          attachmentsToAdd
        );
        if (!uploadResult.success && !attachmentError) {
          attachmentError =
            uploadResult.error || "Failed to upload new post attachments";
        } else if (uploadResult.success) {
          attachmentsChanged = true;
        }
      }

      if (attachmentsChanged || !updatedPost.attachments) {
        const refreshedPost = await this.getPostById(id);
        if (refreshedPost) {
          updatedPost = refreshedPost;
        }
      }

      return {
        success: true,
        data: updatedPost,
        ...(attachmentError ? { error: attachmentError } : {}),
      };
    } catch (err) {
      return failure(err, "Failed to update post");
    }
  }

  // Delete post
  static async deletePost(id: number): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/posts/${id}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete post");
    }
  }

  // Like post
  static async likePost(id: number): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.post(`/posts/${id}/like`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to like post");
    }
  }

  // Unlike post
  static async unlikePost(id: number): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/posts/${id}/like`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to unlike post");
    }
  }

  // Pin post
  static async pinPost(id: number): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.post(`/posts/${id}/pin`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to pin post");
    }
  }

  // Unpin post
  static async unpinPost(id: number): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/posts/${id}/pin`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to unpin post");
    }
  }

  // Upload attachments for a post
  static async uploadPostAttachments(
    postId: number,
    files: File[]
  ): Promise<PostServiceResponse<PostAttachment[]>> {
    try {
      if (!files || files.length === 0) {
        return { success: true, data: [] };
      }

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("attachments[]", file, file.name);
      });

      const response = await ApiService.post(
        `/files/community/posts/${postId}/upload-attachment`,
        formData
      );
      const result = await ApiService.handleResponse<{
        data: PostAttachment[];
      }>(response);

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to upload post attachments");
    }
  }

  // Delete a specific attachment
  static async deletePostAttachment(
    attachmentId: number
  ): Promise<PostServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/files/delete/${attachmentId}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete post attachment");
    }
  }

  // Get paginated comments for a post
  static async getPostComments(
    postId: number,
    page: number = 1
  ): Promise<CommentPaginatedResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      const response = await ApiService.get(
        `/posts/${postId}/comments?${params.toString()}`
      );
      const result: CommentPaginatedResponse = await ApiService.handleResponse(
        response
      );

      return result;
    } catch (error) {
      logger.error("Error fetching comments:", error);
      return null;
    }
  }

  // Create new comment
  static async createComment(
    postId: number,
    commentData: CreateCommentInput
  ): Promise<CommentServiceResponse<Comment>> {
    try {
      const { attachments = [], ...payload } = commentData;

      const response = await ApiService.post(
        `/posts/${postId}/comments`,
        payload
      );
      const result: CommentDetailResponse = await ApiService.handleResponse(
        response
      );

      let createdComment = result.data;
      let attachmentError: string | undefined;

      if (attachments.length > 0 && createdComment.id) {
        const uploadResult = await this.uploadCommentAttachments(
          createdComment.id,
          attachments
        );

        if (!uploadResult.success) {
          attachmentError =
            uploadResult.error || "Failed to upload comment attachments";
        } else if (uploadResult.data && uploadResult.data.length > 0) {
          createdComment = {
            ...createdComment,
            attachments: [
              ...(createdComment.attachments || []),
              ...uploadResult.data,
            ],
          };
        }
      }

      return {
        success: true,
        data: createdComment,
        ...(attachmentError ? { error: attachmentError } : {}),
      };
    } catch (err) {
      return failure(err, "Failed to create comment");
    }
  }

  // Update existing comment
  static async updateComment(
    postId: number,
    commentId: number,
    updates: UpdateCommentInput
  ): Promise<CommentServiceResponse<Comment>> {
    try {
      const {
        attachmentsToAdd = [],
        attachmentsToRemove = [],
        ...payload
      } = updates;

      let attachmentError: string | undefined;

      const response = await ApiService.patch(
        `/posts/${postId}/comments/${commentId}`,
        payload
      );
      const result: CommentDetailResponse = await ApiService.handleResponse(
        response
      );

      let updatedComment = result.data;

      if (attachmentsToRemove.length > 0) {
        for (const attachmentId of attachmentsToRemove) {
          const deleteResult = await this.deleteCommentAttachment(attachmentId);
          if (!deleteResult.success && !attachmentError) {
            attachmentError =
              deleteResult.error || "Failed to delete comment attachment";
          }
        }
      }

      if (updatedComment.attachments && attachmentsToRemove.length > 0) {
        const removedIds = new Set(attachmentsToRemove);
        updatedComment = {
          ...updatedComment,
          attachments: updatedComment.attachments.filter(
            (a) => !removedIds.has(a.id)
          ),
        };
      }

      if (attachmentsToAdd.length > 0) {
        const uploadResult = await this.uploadCommentAttachments(
          commentId,
          attachmentsToAdd
        );
        if (!uploadResult.success && !attachmentError) {
          attachmentError =
            uploadResult.error || "Failed to upload new comment attachments";
        } else if (uploadResult.success && uploadResult.data) {
          updatedComment = {
            ...updatedComment,
            attachments: [
              ...(updatedComment.attachments || []),
              ...uploadResult.data,
            ],
          };
        }
      }

      return {
        success: true,
        data: updatedComment,
        ...(attachmentError ? { error: attachmentError } : {}),
      };
    } catch (err) {
      return failure(err, "Failed to update comment");
    }
  }

  // Upload attachments for a comment
  static async uploadCommentAttachments(
    commentId: number,
    files: File[]
  ): Promise<CommentServiceResponse<PostAttachment[]>> {
    try {
      if (!files || files.length === 0) {
        return { success: true, data: [] };
      }

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("attachments[]", file, file.name);
      });

      const response = await ApiService.post(
        `/files/community/comments/${commentId}/upload-attachment`,
        formData
      );
      const result = await ApiService.handleResponse<{
        data: PostAttachment[];
      }>(response);

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to upload comment attachments");
    }
  }

  // Delete a specific comment attachment
  static async deleteCommentAttachment(
    attachmentId: number
  ): Promise<CommentServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/files/delete/${attachmentId}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete comment attachment");
    }
  }

  // Delete comment
  static async deleteComment(
    postId: number,
    commentId: number
  ): Promise<CommentServiceResponse<void>> {
    try {
      const response = await ApiService.delete(
        `/posts/${postId}/comments/${commentId}`
      );
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete comment");
    }
  }

  // Like comment
  static async likeComment(
    commentId: number
  ): Promise<CommentServiceResponse<void>> {
    try {
      const response = await ApiService.post(`/comments/${commentId}/like`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to like comment");
    }
  }

  // Unlike comment
  static async unlikeComment(
    commentId: number
  ): Promise<CommentServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/comments/${commentId}/like`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to unlike comment");
    }
  }

  // Get paginated reported posts list
  static async getReportedPosts(
    page: number = 1
  ): Promise<ReportedPostsResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      const response = await ApiService.get(
        `/admin/community/reported-posts?${params.toString()}`
      );
      const result: ReportedPostsResponse = await ApiService.handleResponse(
        response
      );

      return result;
    } catch (error) {
      logger.error("Error fetching reported posts:", error);
      return null;
    }
  }
}
